import { Box, DialogActions, FormHelperText, Grid } from "@mui/material"
import Button from "@mui/material/Button"
import { ChangeEvent, useRef, useState } from "react"
import { ProductDto } from "@custom-types/products"
import { usePutProductGraphTagsMutation } from "@store/services/productsService"
import Dialog from "@components/Dialog"
import GraphTagsForm, {
  GRAPH_TAGS_FORM_ID,
  GraphTagsFormRef,
  GraphTagsFormValues,
} from "@features/administration/products/EditProduct/GraphTagsForm/GraphTagsForm"
import { formValuesToGraphTagsFileContent } from "@features/administration/products/EditProduct/graphTagsHelper"
import { SubmitErrorHandler, SubmitHandler } from "react-hook-form"
import { enqueueSnackbar } from "notistack"
import TopActionsBar from "@features/administration/products/EditProduct/TopActionsBar"

interface Props {
  onClose: () => void
  product: ProductDto
}

export default function EditGraphTagsDialog({ onClose, product }: Props) {
  const [globalErrorMessages, setGlobalErrorMessages] = useState<string[]>([])
  const formRef = useRef<GraphTagsFormRef>(null)
  const [putProductGraphTags] = usePutProductGraphTagsMutation()

  const handleAddTagRow = () => {
    formRef.current?.addTag({ name: "", rule: "Never", description: "" })
  }

  const handleSubmit: SubmitHandler<GraphTagsFormValues> = async (values) => {
    try {
      await putProductGraphTags({
        key: product.key,
        orgKey: product.organisation_key,
        divKey: product.division_key,
        data: formValuesToGraphTagsFileContent(values),
      }).unwrap()
      onClose()
      enqueueSnackbar("Tags saved successfully", { variant: "success" })
    } catch (error) {
      enqueueSnackbar("Something went wrong while saving the tags", { variant: "error" })
    }
  }

  const handleSubmitError: SubmitErrorHandler<GraphTagsFormValues> = (errors) => {
    const rows = errors?.rows ?? {}
    const errorRowsNumbers = Object.keys(rows)
      .filter((key) => key !== "root")
      .map((key) => Number(key) + 1)

    const messages: string[] = []

    if (rows.root?.message) {
      messages.push(rows.root.message)
    }

    if (errorRowsNumbers.length) {
      messages.push(`There are errors in rows: ${errorRowsNumbers.join(", ")}`)
    }

    setGlobalErrorMessages(messages)
  }

  const handleCancel = () => onClose()

  const handleImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    formRef.current?.importTagsFile(file)
  }

  const handleExport = () => formRef.current?.exportTagsFile()

  return (
    <Dialog
      title="Edit graph tags"
      actionsComponent={
        <DialogActions sx={{ width: "100%", p: 0 }}>
          <Box sx={{ flexGrow: 1 }}>
            {globalErrorMessages.map((error, index) => (
              <FormHelperText error key={`${error}-${index}`}>
                - {error}
              </FormHelperText>
            ))}
          </Box>

          <Button color="error" variant="contained" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form={GRAPH_TAGS_FORM_ID}>
            Save
          </Button>
        </DialogActions>
      }
      open
      onClose={onClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
    >
      <Grid container gap={1} direction="column">
        <Grid item>
          <TopActionsBar
            onAddTagRowClick={handleAddTagRow}
            onImportClick={handleImport}
            onExportClick={handleExport}
          />
        </Grid>

        <Grid item>
          <GraphTagsForm
            ref={formRef}
            product={product}
            onSubmit={handleSubmit}
            onSubmitError={handleSubmitError}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}
